import React from 'react';
import pt from 'prop-types';

import isObject from 'lodash/isObject';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';

import { ModalsContext } from 'client/src/context/modals';
import StorefrontEditorIcon from 'components/storefront/editor/icon';
import SwellColorSchemePreview from './color-scheme-preview';
import SwellColorSchemePanel, {
  MODAL_PROCESS_SCHEME_ID,
} from './color-scheme-panel';

function SwellColorSchemeGroup({ value, field, onChange }) {
  const [selected, setSelected] = React.useState('');
  const { fields } = field.params;

  const onSchemeClick = React.useCallback(
    /** @param {React.MouseEvent} event */
    (event) => {
      setSelected(event.currentTarget.dataset.id);
    },
    [],
  );

  const onSchemeDelete = React.useCallback(
    (updatedValue) => {
      onChange(updatedValue);
      setSelected('');
    },
    [onChange],
  );

  const onSchemeDuplicate = React.useCallback(
    (schemeId, updatedValue) => {
      onChange(updatedValue);
      setSelected(schemeId);
    },
    [onChange],
  );

  const onPanelClose = React.useCallback(() => {
    setSelected('');
  }, []);

  const onSchemeChange = React.useCallback(
    (schemeId, data) => {
      const updatedValue = {
        ...value,
        [schemeId]: {
          ...value?.[schemeId],
          settings: {
            ...value?.[schemeId]?.settings,
            ...data,
          },
        },
      };

      onChange(updatedValue);
    },
    [value, onChange],
  );

  const onSchemeValueChange = React.useCallback(
    (data) => {
      onSchemeChange(selected, data);
    },
    [selected, onSchemeChange],
  );

  const modalsCtx = React.useContext(ModalsContext);

  const onSchemeSubmit = React.useCallback(
    (values) => {
      const schemeId = kebabCase(values.name);

      const scheme = field.params.fields.reduce(
        (acc, field) => {
          acc[field.id] = field.default ?? '';
          return acc;
        },
        { label: values.name },
      );

      onSchemeChange(schemeId, scheme);

      modalsCtx.closeModal(MODAL_PROCESS_SCHEME_ID);

      setSelected(schemeId);
    },
    [field, onSchemeChange, modalsCtx],
  );

  const nextColorSchemeName = React.useMemo(() => {
    if (!value) {
      return 'scheme-1';
    }

    for (let index = 1; ; ++index) {
      const schemeId = `scheme-${index}`;

      if (!Object.hasOwn(value, schemeId)) {
        return startCase(schemeId);
      }
    }
  }, [value]);

  const onAddScheme = React.useCallback(() => {
    onSchemeSubmit({
      name: nextColorSchemeName,
    });
  }, [onSchemeSubmit, nextColorSchemeName]);

  return (
    <div className="storefront-editor-widget">
      {!value || isObject(value) ? (
        <div className="storefront-editor--color-scheme-group">
          {value &&
            Object.entries(value).map(([schemeId, scheme]) => {
              const { settings } = scheme;

              return (
                <SwellColorSchemePreview
                  key={schemeId}
                  schemeId={schemeId}
                  settings={settings}
                  onSchemeClick={onSchemeClick}
                />
              );
            })}

          <button
            className="color-scheme-add"
            onClick={onAddScheme}
            type="button"
          >
            <StorefrontEditorIcon type="plus" width={16} height={16} />

            <span>Add scheme</span>
          </button>
        </div>
      ) : (
        <>
          {field.label} does not contain a valid color scheme group
          configuration. Refer to the [color scheme guide] for details.
        </>
      )}

      <SwellColorSchemePanel
        selected={selected}
        value={value}
        fields={fields}
        onSchemeChange={onSchemeValueChange}
        onSchemeDuplicate={onSchemeDuplicate}
        onSchemeDelete={onSchemeDelete}
        onPanelClose={onPanelClose}
      />
    </div>
  );
}

SwellColorSchemeGroup.propTypes = {
  value: pt.oneOfType([pt.object, pt.array]),
  field: pt.object.isRequired,

  onChange: pt.func.isRequired,
};

export default React.memo(SwellColorSchemeGroup);
